import { onlineManager } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';

/**
 * Make Capacitor's Network plugin the source of truth for React Query's
 * `onlineManager` on native platforms.
 *
 * `onlineManager` is what React Query consults to decide whether a query may
 * fetch and — crucially for offline-first — whether a mutation should run now or
 * be *paused* until connectivity returns. By default it listens to
 * `navigator.onLine`, which is unreliable inside the iOS/Android WebView. We
 * replace that listener with the OS-level network state from @capacitor/network.
 *
 * On web we intentionally do nothing: the browser's `navigator.onLine` + online/
 * offline events that React Query uses by default are accurate there.
 *
 * Call once, before the QueryClient starts being used.
 */
export function initOnlineManager(): void {
  if (!Capacitor.isNativePlatform()) return;

  onlineManager.setEventListener((setOnline) => {
    // Seed the current state immediately — the listener below only fires on
    // *changes*, so without this the app would assume the RQ default until the
    // first transition.
    Network.getStatus()
      .then((status) => setOnline(status.connected))
      .catch(() => {
        /* If the plugin can't report, leave RQ's last-known state as-is. */
      });

    const handlePromise = Network.addListener('networkStatusChange', (status) => {
      setOnline(status.connected);
    });

    // React Query expects a synchronous cleanup function; the Capacitor handle
    // resolves asynchronously, so remove it once it's available.
    return () => {
      handlePromise.then((handle) => handle.remove()).catch(() => {});
    };
  });
}
